import { SCANDINAVIAN_COUNTRIES } from '@components/CountriesFormField/CountriesFormField'
import { getCountries, getCountryCallingCode } from 'react-phone-number-input'
import type { PhoneCollectionModel } from '@/types/Common/PhoneCollectionModel'
import type { PhoneNumberModel } from '@/types/Common/PhoneNumberModel'
import type { PhoneInfo } from '@/types/Phone'

/**
 * Creates a mapping from country calling codes to country codes
 * This reuses the same logic as PhoneNumberInput component
 */
const createCallingCodeToCountryMap = (allowAllCountries = false): Map<string, string> => {
  const map = new Map<string, string>()

  const countries = getCountries().filter((country) => {
    if (!allowAllCountries) {
      return SCANDINAVIAN_COUNTRIES.includes(country)
    }
    return true
  })

  countries.forEach((country) => {
    const callingCode = getCountryCallingCode(country)
    map.set(callingCode, country)
  })

  return map
}

/**
 * Parses a phone number string into country code and number.
 * Used when processing raw string inputs (e.g. from forms) into structured data.
 *
 * @param phoneNumber - The raw phone number string to parse
 * @param allowAllCountries - Whether to allow all countries or just Scandinavian ones
 * @returns An object with countryCode and number
 */
export const parsePhoneNumber = (phoneNumber: string, allowAllCountries = false): PhoneInfo => {
  if (!phoneNumber) {
    return { countryCode: '+47', number: null }
  }

  const cleanPhone = phoneNumber.replace(/\s/g, '')
  const callingCodeMap = createCallingCodeToCountryMap(allowAllCountries)

  if (cleanPhone.startsWith('+')) {
    // Find the country code by checking all available calling codes
    for (const [callingCode] of callingCodeMap) {
      if (cleanPhone.startsWith(`+${callingCode}`)) {
        const number = cleanPhone.slice(`+${callingCode}`.length)
        return {
          countryCode: `+${callingCode}`,
          number: number || null,
        }
      }
    }

    // Fallback: assume +47 for Norwegian numbers
    const number = cleanPhone.slice(1)
    return {
      countryCode: '+47',
      number: number || null,
    }
  }

  // If no country code, assume Norwegian number
  return {
    countryCode: '+47',
    number: cleanPhone || null,
  }
}

export function toPhoneNumberModel(phone: PhoneInfo | null | undefined): PhoneNumberModel | null {
  if (!phone) return null
  const number = (phone.number ?? '').trim()
  if (!number) return null
  return { countryCode: phone.countryCode, number }
}

export function getAnyPhoneNumberModel(
  phoneCollection: PhoneCollectionModel | null | undefined,
): PhoneNumberModel | null {
  if (!phoneCollection) return null
  return phoneCollection.mobile ?? phoneCollection.landline ?? null
}

/**
 * Converts a single phone info object into an API-ready phone collection.
 */
export function toPhoneCollectionModel(
  phone: PhoneInfo | null | undefined,
): PhoneCollectionModel | null {
  const apiPhone = toPhoneNumberModel(phone)
  if (!apiPhone) return null
  return {
    mobile: apiPhone,
    landline: apiPhone,
  }
}

/**
 * Parses a raw phone string directly into an API-ready format.
 * Returns undefined if the input is empty or invalid.
 */
export function parseToPhoneNumberModel(
  phoneNumber: string | null | undefined,
  allowAllCountries = false,
): PhoneNumberModel | undefined {
  if (!phoneNumber?.trim()) return undefined
  const parsed = parsePhoneNumber(phoneNumber, allowAllCountries)
  return toPhoneNumberModel(parsed) ?? undefined
}

/**
 * Parses a raw phone string directly into an API-ready collection format.
 * Returns null if the input is empty or invalid.
 */
export function parseToPhoneCollectionModel(
  phoneNumber: string | null | undefined,
  allowAllCountries = false,
): PhoneCollectionModel | null {
  if (!phoneNumber?.trim()) return null
  const parsed = parsePhoneNumber(phoneNumber, allowAllCountries)
  return toPhoneCollectionModel(parsed)
}

/**
 * Formats a phone number with spaces between groups.
 * Handles both Norwegian (+47) and international formats.
 * Examples:
 * - "+47 91 23 45 67" (Norway)
 * - "+46 701 23 45 67" (Sweden)
 * - "91 23 45 67" (without country code, assumes +47)
 */
export const formatPhoneNumber = (value: string): string => {
  if (!value) {
    return ''
  }

  // Remove all spaces first
  const cleaned = value.replace(/\s/g, '')

  // Extract country code if present
  if (cleaned.startsWith('+')) {
    // Format: +CC NNNNNNNNNN -> +CC NN NN NN NN (or similar grouping)
    // Try to match country codes: most are 1-2 digits, some are 3 digits
    // Try 2 digits first (most common: +47, +46, +45, etc.), then 1 digit, then 3 digits
    let match = /^(\+\d{2})(\d+)$/.exec(cleaned)
    match ??= /^(\+\d{1})(\d+)$/.exec(cleaned)
    match ??= /^(\+\d{3})(\d+)$/.exec(cleaned)

    if (match) {
      const countryCode = match[1]
      const nationalNumber = match[2]

      // Group national number into pairs of digits with spaces
      const groupedNational = nationalNumber.match(/.{1,2}/g) ?? []
      const formatted = groupedNational.join(' ')

      return `${countryCode} ${formatted}`.trim()
    }
  }

  // If no country code, group into pairs with spaces
  const grouped = cleaned.match(/.{1,2}/g) ?? []
  return grouped.join(' ')
}

/**
 * Alternative phone formatter that trims any leading country code
 * by keeping only the last 8 digits, then groups them into pairs.
 * Example: "+47 91234567" -> "91 23 45 67" (country code removed)
 */
export const formatPhoneNumberTrimCountry = (value: string): string => {
  if (!value) return ''

  // Remove all non-digit characters
  let digitsOnly = value.replace(/\D/g, '')

  // Keep only the last 8 digits (trim country code / extra leading digits)
  if (digitsOnly.length > 8) {
    digitsOnly = digitsOnly.slice(-8)
  }

  // Group into pairs with spaces
  const grouped = digitsOnly.match(/.{1,2}/g) ?? []
  return grouped.join(' ')
}

interface PhoneDisplayInput {
  countryCode?: string | null
  number?: string | null
}

/**
 * Formats a phone object for display.
 * Returns formatted phone number or fallback text.
 */
export const formatPhoneDisplay = (phone?: PhoneDisplayInput | null, fallback = '-'): string => {
  if (!phone?.number) {
    return fallback
  }
  return formatPhoneNumber(`${phone.countryCode || ''}${phone.number}`)
}
